import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  FeatureList,
  FormatChip,
  Reveal,
  Section,
  SectionIntro,
  SectionLabel,
  ShowcaseCardGrid,
  SourceGroupGrid,
} from './primitives'
import {
  AssistantVisual,
  BreadthVisual,
  DepthVisual,
  GraphThumb,
  MapThumb,
  OsintReachVisual,
  ResearchVisual,
  SchemaFlowVisual,
  TimelineThumb,
} from './visuals'

const STATS = [
  { value: '176+', label: 'languages understood' },
  { value: '∞', label: 'schemas you can define' },
  { value: '100%', label: 'on your infrastructure' },
  { value: '0', label: 'documents leave your network' },
]

export function StatStrip() {
  return (
    <Reveal>
      <div className="grid grid-cols-2 divide-border/60 border-y border-border/60 md:grid-cols-4 md:divide-x">
        {STATS.map(stat => (
          <div key={stat.label} className="px-6 py-10 md:px-10">
            <div className="font-brand text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
              {stat.value}
            </div>
            <div className="mt-2 font-mono text-2xs uppercase tracking-[0.2em] text-muted-foreground">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </Reveal>
  )
}

export function ExtractionSection() {
  return (
    <Section id="extraction">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div className="space-y-12">
          <SectionIntro
            index="01"
            label="Schema-driven extraction"
            title={
              <>
                Your schema.
                <br />
                Your graph.
              </>
            }
            lede="Tell Xendrel what a fact looks like in your world — people, companies, payments, shipments, incidents — and that is exactly what it extracts. No noise, no bloated dumps: a clean, typed graph in the shape you designed."
          />
          <FeatureList
            items={[
              {
                title: 'Design your own schema',
                description:
                  'Define the entity types, relationships, and attributes that matter to your work, then let extraction fill them across every file.',
              },
              {
                title: 'Or start from ours',
                description:
                  'Ready-made starter schemas for finance, audit, business intelligence, and investigations get you extracting in minutes.',
              },
              {
                title: 'Format and normalize on the way in',
                description:
                  'Decide how extracted values should look — dates, names, amounts — and Xendrel formats and normalizes them consistently across every source.',
              },
              {
                title: 'Evolve as you learn',
                description:
                  'Fork, extend, import, and export schemas as a case grows — the graph follows your thinking.',
              },
              {
                title: 'Skip the blob',
                description:
                  'Capture only the information and relations you asked for, and leave the irrelevant bulk of every document behind.',
              },
            ]}
          />
        </div>
        <Reveal delay={0.1}>
          <SchemaFlowVisual className="mx-auto w-full max-w-md lg:max-w-none" />
        </Reveal>
      </div>
    </Section>
  )
}

const PIPELINES = [
  {
    kicker: 'Depth',
    title: 'Xendrel Knowledge Graph',
    body: 'Agents read every document and build a typed knowledge graph — entities, relationships, attributes — in the shape your schema defines, with each fact linked back to its source.',
    visual: DepthVisual,
  },
  {
    kicker: 'Breadth',
    title: 'Xendrel Datalake',
    body: 'Digitization at scale: every file is parsed, chunked, and vectorized on arrival. Search it by meaning or by close-matching text — even when the wording only resembles your query — or hand it to the agents. Nothing waits for a full extraction.',
    visual: BreadthVisual,
  },
]

export function PipelinesSection() {
  return (
    <Section id="pipelines">
      <div className="space-y-16">
        <SectionIntro
          index="02"
          label="Depth & breadth"
          title="Two speeds. One platform."
          lede="Some questions need a deep, structured graph. Others just need every file instantly searchable. Xendrel runs both, side by side, over the same data."
        />
        <ShowcaseCardGrid items={PIPELINES} />
      </div>
    </Section>
  )
}

const ONPREM_POINTS = [
  {
    title: 'Your hardware, your rules',
    description: 'Deploy on the servers you already control — from a single machine to a cluster.',
  },
  {
    title: 'Isolated by design',
    description:
      'Research agents reason inside the same sealed environment your documents live in.',
  },
  {
    title: 'Air-gap ready',
    description: 'Built to operate with outbound traffic locked down to zero.',
  },
  {
    title: 'Every byte accounted for',
    description: 'Documents, indexes, graphs, and reports never cross your perimeter.',
  },
]

export function OnPremiseSection() {
  return (
    <Section id="on-premise">
      <Reveal>
        <div className="rounded-3xl bg-foreground px-8 py-16 text-background md:px-16 md:py-20">
          <SectionLabel index="03" tone="inverse">
            Completely on-premise
          </SectionLabel>
          <h2 className="mt-6 max-w-2xl font-brand text-4xl font-semibold tracking-tight md:text-5xl">
            Your data never leaves.
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-background/70">
            Xendrel runs entirely on your infrastructure — the models, the storage, the search, the
            agents. No cloud services, no phoning home, no third parties in the loop.
          </p>
          <FeatureList items={ONPREM_POINTS} tone="inverse" className="mt-12" />
          <p className="mt-12 max-w-2xl border-l border-background/25 pl-5 text-sm leading-relaxed text-background/60">
            There is exactly one way out, and you open it deliberately: the OSINT agent steps
            outside to query public registers.{' '}
            <a href="#osint" className="text-background underline underline-offset-4">
              See how that is contained ↓
            </a>
          </p>
        </div>
      </Reveal>
    </Section>
  )
}

const LANGUAGE_SAMPLES =
  'Slovenčina · English · Deutsch · 中文 · العربية · Español · Українська · हिन्दी · Français · 日本語 · Português · Polski'

export function LanguagesSection() {
  return (
    <Section id="languages">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <Reveal className="order-last lg:order-first">
          <Card className="rounded-2xl p-10 text-center md:p-14">
            <div className="font-brand text-7xl font-semibold tracking-tight text-foreground md:text-8xl">
              176<span className="text-primary">+</span>
            </div>
            <div className="mt-3 font-mono text-2xs uppercase tracking-[0.3em] text-muted-foreground">
              languages, understood
            </div>
            <p className="mt-8 text-sm leading-relaxed text-muted-foreground">{LANGUAGE_SAMPLES}</p>
          </Card>
        </Reveal>
        <div className="space-y-12">
          <SectionIntro
            index="04"
            label="Multilingual understanding"
            title="Understood — not just processed."
            lede="Xendrel's agents understand what a document says — in any of 176+ languages. Multilingual understanding is built into every layer — reading, search, reasoning, and reporting — so you can ask in one language and get grounded answers from documents written in another."
          />
          <FeatureList
            items={[
              {
                title: 'Cross-language search',
                description:
                  'Meaning-based search finds the right passages even when your query and the source share no words.',
              },
              {
                title: 'Cross-language answers',
                description:
                  'Agents reason over mixed-language evidence and reply in the language you choose.',
              },
            ]}
          />
        </div>
      </div>
    </Section>
  )
}

const AGENTS = [
  {
    kicker: 'On demand',
    title: 'Xendrel Assistant',
    body: 'A conversational analyst that knows your data. Ask in plain language and get grounded answers with citations pointing at the exact documents — then pull any thread deeper, straight from the chat.',
    visual: AssistantVisual,
  },
  {
    kicker: 'In depth',
    title: 'Deep Research',
    body: 'Hand over a question and an autonomous agent plans the investigation itself — searching, cross-checking, weighing evidence — then returns a structured, fully cited report of everything it found.',
    visual: ResearchVisual,
  },
]

export function AgentsSection() {
  return (
    <Section id="research">
      <div className="space-y-16">
        <SectionIntro
          index="05"
          label="Deep research & assistant"
          title="Agents that do the reading."
          lede="Two agents share the work: the Assistant answers on the spot, Deep Research goes away and investigates. Both stay inside your isolated, on-premise environment — and both show their sources."
        />
        <ShowcaseCardGrid items={AGENTS} />
        <FeatureList
          className="lg:grid-cols-3"
          items={[
            {
              title: 'Cited, not asserted',
              description:
                'Every claim in a report links to the exact document and passage it came from.',
            },
            {
              title: 'Findings you can act on',
              description:
                'Reports arrive structured — summary, findings, evidence — ready to hand to a colleague.',
            },
            {
              title: 'Sealed while it thinks',
              description:
                'Research runs where your data lives — even the reasoning never leaves your environment.',
            },
          ]}
        />
      </div>
    </Section>
  )
}

const OSINT_SOURCES = [
  {
    title: 'Company registers',
    scope: 'CZ · SK',
    description:
      'The official record: legal form, registered address, status, officers, and owners — read straight from the register, not from a summary of it. It also runs the lookup in reverse: give it a person and it returns every company they sit in.',
    sources: ['ARES', 'Obchodní rejstřík', 'RPO', 'ORSR'],
  },
  {
    title: 'Insolvency & debt',
    scope: 'CZ · SK',
    description:
      'Whether a company actually pays what it owes — insolvency proceedings, unreliable-VAT-payer status, and outstanding social- and health-insurance obligations, with the amounts the registers publish.',
    sources: ['ISIR', 'Unreliable VAT payers', 'Sociálna poisťovňa', 'VšZP', 'Dôvera'],
  },
  {
    title: 'Sanctions screening',
    scope: 'EU · US · UN',
    description:
      'Every subject is screened against the consolidated designation lists — primary names and every recorded alias spelling, so a transliteration does not slip a match.',
    sources: ['EU consolidated list', 'OFAC SDN', 'UN Security Council'],
  },
  {
    title: 'The open web',
    scope: 'Live',
    description:
      'Everything the registers do not hold: news, filings, official publications, and press mentions — searched live and read in whatever language they were written in.',
    sources: ['News', 'Filings', 'Official publications', 'Press mentions'],
  },
]

const OSINT_POINTS = [
  {
    title: 'It asks instead of guessing',
    description:
      'Two people share a name? The run pauses and shows you the actual register records to choose between, rather than quietly picking one.',
  },
  {
    title: 'Unreachable is never “clean”',
    description:
      'When a register cannot be reached, the report says the source was not checked. A silent gap never turns into a green light.',
  },
  {
    title: 'Every claim keeps its link',
    description:
      'Findings carry the register record’s own address and the moment it was retrieved, cited inline throughout the report.',
  },
  {
    title: 'Straight into your graph',
    description:
      'The agent proposes the entities and relationships its report supports, shaped by your schema. You tick what is real and import it.',
  },
  {
    title: 'Finishes as a document',
    description:
      'The completed investigation exports as a PDF report — ready to hand to a colleague or file with the case.',
  },
  {
    title: 'It goes out only when you send it',
    description:
      'Reaching the internet is an explicit, acknowledged action, taken per case. Everything else on the platform stays sealed.',
  },
]

export function OsintSection() {
  return (
    <Section id="osint">
      <div className="space-y-16">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <SectionIntro
            index="06"
            label="Open-source intelligence"
            title="When the answer isn't in your files."
            lede="Everything else on this page works on the documents you own. The OSINT agent works on everything else: hand it a name and it goes out to official company registers, insolvency and debtor lists, and international sanctions lists — then comes back with a cited report and a set of entities ready to drop into your graph."
          />
          <Reveal delay={0.1}>
            <OsintReachVisual className="mx-auto w-full max-w-md lg:max-w-none" />
          </Reveal>
        </div>
        <SourceGroupGrid groups={OSINT_SOURCES} />
        <Reveal>
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Sanctions screening and open-web research are global. The typed register adapters cover
            Czechia and Slovakia today — each further jurisdiction plugs into the same pattern.
          </p>
        </Reveal>
        <FeatureList className="lg:grid-cols-3" items={OSINT_POINTS} />
      </div>
    </Section>
  )
}

export function ProvenanceSection() {
  return (
    <Section id="provenance">
      <div className="space-y-12">
        <SectionIntro
          index="07"
          label="Knowledge you can defend"
          title="Every answer keeps its receipts."
          lede="Store your knowledge graphs, hunt for connections across everything you've extracted, and jump from any entity, relationship, or claim straight back to the original document it came from. Decisions stay informed — and defensible."
        />
        <FeatureList
          items={[
            {
              title: 'Persistent knowledge graphs',
              description:
                'Graphs are durable working assets — return to them, extend them, and build on past work.',
            },
            {
              title: 'Connection discovery',
              description:
                'Surface non-obvious paths between people, organizations, events, and assets.',
            },
            {
              title: 'One click to the source',
              description:
                'Open the exact page a fact was extracted from, right from the graph or a report.',
            },
            {
              title: 'Audit-friendly by default',
              description: 'The trail from upload to conclusion stays reviewable from end to end.',
            },
          ]}
        />
      </div>
    </Section>
  )
}

const FORMAT_GROUPS = [
  { name: 'Documents', formats: ['PDF', 'DOCX', 'DOC', 'TXT', 'MD', 'LOG'] },
  { name: 'Structured data', formats: ['CSV', 'XLSX', 'XLS'] },
  { name: 'Images & scans', formats: ['JPG', 'PNG', 'WEBP'] },
  { name: 'Audio', formats: ['MP3', 'WAV', 'M4A', 'OGG', 'OPUS', 'FLAC', 'AAC'] },
]

export function FormatsSection() {
  return (
    <Section id="formats">
      <div className="space-y-14">
        <SectionIntro
          index="08"
          label="Universal ingestion"
          title="Feed it almost anything."
          lede="Structured or unstructured, pristine or scanned, written or spoken — Xendrel takes it all in and treats it as one body of knowledge."
        />
        <div className="space-y-8">
          {FORMAT_GROUPS.map((group, i) => (
            <Reveal key={group.name} delay={i * 0.05}>
              <div className="grid gap-3 border-t border-border/60 pt-6 md:grid-cols-[200px_1fr] md:gap-8">
                <div className="font-mono text-2xs uppercase tracking-[0.25em] text-muted-foreground">
                  {group.name}
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.formats.map(f => (
                    <FormatChip key={f}>{f}</FormatChip>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Scanned and photographed documents are read automatically. Audio recordings are
            transcribed with speaker attribution, in the language they were spoken in — then flow
            through the same extraction and search as everything else. And this list keeps growing:
            new formats join the same pipeline as they land.
          </p>
        </Reveal>
      </div>
    </Section>
  )
}

const VIEWS = [
  {
    title: 'Knowledge Graph',
    body: 'Explore entities and relationships as an interactive network — in 2D or full 3D — and interrogate any node down to its source.',
    thumb: GraphThumb,
  },
  {
    title: 'Timeline',
    body: 'Project every dated fact onto a navigable time axis and watch sequences of events take shape.',
    thumb: TimelineThumb,
  },
  {
    title: 'Live Map',
    body: 'Replay movement as animated tracks, draw watched zones around places that matter, and get alerted when a track enters one.',
    thumb: MapThumb,
  },
]

export function ViewsSection() {
  return (
    <Section id="views">
      <div className="space-y-16">
        <SectionIntro
          index="09"
          label="Ways of seeing"
          title="One dataset. Three instruments."
          lede="The same extracted knowledge, examined from the angles that answer different questions: structure, time, and place."
        />
        <div className="grid gap-6 md:grid-cols-3">
          {VIEWS.map((view, i) => (
            <Reveal key={view.title} delay={i * 0.08}>
              <Card className="flex h-full flex-col overflow-hidden rounded-2xl p-0">
                <view.thumb className="w-full border-b border-border/60" />
                <div className="flex-1 p-7">
                  <h3 className="font-brand text-xl font-semibold tracking-tight text-card-foreground">
                    {view.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                    {view.body}
                  </p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  )
}

export function TeamsSection() {
  return (
    <Section id="teams">
      <div className="space-y-12">
        <SectionIntro
          index="10"
          label="From case to conclusion"
          title="Built for teams. Finished with reports."
          lede="Cases are shared workspaces: bring in colleagues with the right roles, investigate together, and when the work is done, export a detailed PDF report built from what you found."
        />
        <FeatureList
          items={[
            {
              title: 'Multi-user cases',
              description:
                'Owners, collaborators, and viewers work the same case with role-scoped access.',
            },
            {
              title: 'Access that matches sensitivity',
              description:
                'Data access follows the classification labels and sensitivity levels you define.',
            },
            {
              title: 'Template-driven PDF reports',
              description:
                'Polished case summaries and structured exports, generated from the evidence you selected.',
            },
            {
              title: 'A record of the work',
              description: 'Who added what, and when — the whole investigation stays reviewable.',
            },
          ]}
        />
      </div>
    </Section>
  )
}

export function ClosingSection() {
  return (
    <Section id="start" className="border-t-0">
      <Reveal className="mx-auto max-w-3xl py-8 text-center md:py-16">
        <h2 className="font-brand text-4xl font-semibold tracking-tight text-foreground md:text-6xl">
          See what your data has been holding back.
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
          Everything above, running quietly on your own hardware — from the first uploaded file to
          the final report.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg" className="px-10">
            <Link to="/login">Open Xendrel</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="px-8">
            <a href="#extraction">Revisit the capabilities</a>
          </Button>
        </div>
      </Reveal>
    </Section>
  )
}
